import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface NotificationPreferences {
  id?: string;
  profile_id: string;
  nearby_matches: boolean;
  urgent_matches: boolean;
  new_classes: boolean;
  court_promotions: boolean;
  quiet_hours_start: string;
  quiet_hours_end: string;
  max_daily_smart_notifications: number;
}

const DEFAULTS: Omit<NotificationPreferences, 'profile_id'> = {
  nearby_matches: true,
  urgent_matches: true,
  new_classes: true,
  court_promotions: true,
  quiet_hours_start: '22:00',
  quiet_hours_end: '07:00',
  max_daily_smart_notifications: 5,
};

export function useNotificationPreferences() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const { data: preferences, isLoading } = useQuery({
    queryKey: ['notification-preferences', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return null;
      const { data, error } = await supabase
        .from('notification_preferences' as any)
        .select('*')
        .eq('profile_id', profile.id)
        .maybeSingle();
      if (error) throw error;
      return (data as any as NotificationPreferences) || { ...DEFAULTS, profile_id: profile.id };
    },
    enabled: !!profile?.id,
  });

  const updatePreferences = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      if (!profile?.id) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('notification_preferences' as any)
        .upsert(
          {
            profile_id: profile.id,
            ...DEFAULTS,
            ...preferences,
            ...updates,
            updated_at: new Date().toISOString(),
          } as any,
          { onConflict: 'profile_id' }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast.success('Đã lưu cài đặt thông báo thông minh');
    },
    onError: () => {
      toast.error('Không thể lưu cài đặt');
    },
  });

  return {
    preferences: preferences || (profile?.id ? { ...DEFAULTS, profile_id: profile.id } : null),
    isLoading,
    updatePreferences: updatePreferences.mutate,
    isUpdating: updatePreferences.isPending,
  };
}
